import React, { useState, useMemo, useEffect } from "react"
import {
  Card,
  CardBody,
  Table,
  Input,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
} from "reactstrap"
import { FaEdit, FaEye } from "react-icons/fa"
import Breadcrumb from "components/Common/Breadcrumb"
import Loader from "components/Loader/loader"
import Swal from "sweetalert2"
import { fetchData, FetchPutData } from "utils/Common_api_functions"
import { usePermissionData } from "AppContext"
import PermittedComponent from "PermittedComponent"

const ViewQueryModal = ({ open, toggle, selected }) => (
  <Modal isOpen={open} centered size="md" toggle={toggle}>
    <ModalHeader toggle={toggle}>View Query</ModalHeader>
    <ModalBody>
      <FormGroup>
        <Label className="fw-semibold">Query</Label>
        <div
          className="rounded-4 p-3"
          style={{
            background: "rgba(0,0,0,0.03)",
            border: "1px solid rgba(0,0,0,0.1)",
            minHeight: 80,
            fontSize: 14,
            whiteSpace: "pre-wrap",
          }}
        >
          {selected?.query || <span className="text-muted">—</span>}
        </div>
      </FormGroup>
      <FormGroup className="mb-0">
        <Label className="fw-semibold">Remark</Label>
        <div
          className="rounded-4 p-3"
          style={{
            background: "rgba(0,0,0,0.03)",
            border: "1px solid rgba(0,0,0,0.1)",
            minHeight: 60,
            fontSize: 14,
            whiteSpace: "pre-wrap",
          }}
        >
          {selected?.remark || <span className="text-muted">—</span>}
        </div>
      </FormGroup>
    </ModalBody>
    {/* <ModalFooter>
      <Button color="secondary" onClick={toggle}>
        Close
      </Button>
    </ModalFooter> */}
  </Modal>
)

const EditQueryModal = ({ open, toggle, selected, setSelected, onSubmit }) => {
  const [error, setError] = useState("")

  useEffect(() => {
    setError("")
  }, [open])

  const handleSubmit = () => {
    if (!selected?.remark?.trim()) {
      setError("Remark is required")
      return
    }
    onSubmit()
    setError("")
  }

  return (
    <Modal isOpen={open} centered size="l" toggle={toggle}>
      <ModalHeader toggle={toggle}>Edit Remark
        <div>
          <Button className="modal-btn" onClick={handleSubmit}>
            Update
          </Button>
        </div>
      </ModalHeader>
      <ModalBody>
        <Form>
          <FormGroup>
            <Label>
              Remark <span className="text-danger">*</span>
            </Label>
            <Input
              type="textarea"
              rows={4}
              value={selected?.remark || ""}
              onChange={e => {
                setSelected(prev => ({ ...prev, remark: e.target.value }))
                setError("")
              }}
              placeholder="Enter remark"
              className={`rounded-4 ${error ? "is-invalid" : ""}`}
            />
            {error && <div className="invalid-feedback d-block">{error}</div>}
          </FormGroup>
        </Form>
      </ModalBody>
      {/* <ModalFooter>
        <Button className="btn-blue" onClick={handleSubmit}>
          Update
        </Button>
        <Button color="secondary" onClick={toggle}>
          Cancel
        </Button>
      </ModalFooter> */}
    </Modal>
  )
}

const QueriesIndex = () => {
  document.title = "Queries | Siddhi Tax Consultant"

  const { checkAccess } = usePermissionData()

  const [pageSize, setPageSize] = useState(20)
  const [searchString, setSearchString] = useState("")
  const [loading, setLoading] = useState(false)
  const [queries, setQueries] = useState([])

  const [viewModalOpen, setViewModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [selected, setSelected] = useState({})

  useEffect(() => {
    getQueries()
  }, [])

  async function getQueries() {
    const APIdata = await fetchData(setLoading, "query")
    if (APIdata?.success) {
      setQueries(APIdata?.data?.reverse ? APIdata.data.reverse() : APIdata.data)
    }
  }

  const filteredData = useMemo(
    () =>
      queries.filter(
        item =>
          item?.nameOnWhatsapp?.toLowerCase().includes(searchString.toLowerCase()) ||
          item?.whatsappNumber?.includes(searchString)
      ),
    [queries, searchString]
  )

  const [currentPage, setCurrentPage] = useState(1)
  const totalPages = Math.ceil(filteredData.length / pageSize)
  const start = (currentPage - 1) * pageSize

  const paginatedData = useMemo(
    () => filteredData.slice(start, start + pageSize),
    [filteredData, currentPage, pageSize]
  )

  const formatDateTime = isoString => {
    if (!isoString) return "—"
    const d = new Date(isoString)
    const dd = String(d.getDate()).padStart(2, "0")
    const mm = String(d.getMonth() + 1).padStart(2, "0")
    const yyyy = d.getFullYear()
    const hh = String(d.getHours()).padStart(2, "0")
    const min = String(d.getMinutes()).padStart(2, "0")
    return `${dd}-${mm}-${yyyy} ${hh}:${min}`
  }

  const handleUpdate = async () => {
    const APIdata = await FetchPutData(setLoading, "query", selected?.id, {
      remark: selected?.remark,
    })
    if (APIdata?.data?.success) {
      Swal.fire({
        title: "Remark Updated Successfully.",
        text: APIdata?.data?.message,
        icon: "success",
      })
      setEditModalOpen(false)
      setSelected({})
      getQueries()
    } else {
      Swal.fire({
        title: "Error Updating Remark.",
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }

  return (
    <PermittedComponent isAccess={checkAccess(8, "view") || checkAccess(8, "viewglobal")}>
      <div className="page-content">
        {loading && <Loader />}
        <Breadcrumb title="Dashboard" breadcrumbItem="Queries" />

        <Card className="shadow-lg">
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex gap-3">
                <Input
                  type="select"
                  value={pageSize}
                  onChange={e => {
                    setPageSize(Number(e.target.value))
                    setCurrentPage(1)
                  }}
                  className="rounded-4 w-auto pe-5"
                >
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Input>

                <Input
                  className="rounded-4"
                  placeholder="Search by name or phone..."
                  value={searchString}
                  onChange={e => setSearchString(e.target.value)}
                />
              </div>
            </div>

            <Table bordered responsive>
              <thead className="table-dark">
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Phone Number</th>
                  <th>Created At</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center">
                      No queries found
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((item, index) => (
                    <tr key={item.id}>
                      <td>{start + index + 1}</td>
                      <td className="fw-bold">{item?.nameOnWhatsapp}</td>
                      <td>{item?.whatsappNumber}</td>
                      <td>{formatDateTime(item?.createdAt)}</td>
                      <td className="text-center">
                        {(checkAccess(8, "view") || checkAccess(8, "viewglobal")) && (
                          <Button
                            className="bg-secondary text-white me-2 border-0 shadow-none"
                            onClick={() => {
                              setSelected({ ...item })
                              setViewModalOpen(true)
                            }}
                          >
                            <FaEye size={16} />
                          </Button>
                        )}
                        {checkAccess(8, "edit") && (
                          <Button
                            className="bg-success text-white border-0 shadow-none"
                            onClick={() => {
                              setSelected({ ...item })
                              setEditModalOpen(true)
                            }}
                          >
                            <FaEdit size={16} />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>

            <nav
              aria-label="Page navigation"
              className="d-flex justify-content-between align-items-center mt-3"
            >
              <h5 className="mb-0">
                Showing {start + 1} to{" "}
                {Math.min(start + pageSize, filteredData.length)} of{" "}
                {queries.length} entries
              </h5>
              <ul className="pagination mb-0">
                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  >
                    Previous
                  </button>
                </li>

                {[...Array(totalPages)].map((_, i) => {
                  const pageNum = i + 1
                  return (
                    <li
                      key={pageNum}
                      className={`page-item ${pageNum === currentPage ? "active" : ""}`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    </li>
                  )
                })}

                <li
                  className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </CardBody>
        </Card>

        <ViewQueryModal
          open={viewModalOpen}
          toggle={() => setViewModalOpen(false)}
          selected={selected}
        />

        <EditQueryModal
          open={editModalOpen}
          toggle={() => setEditModalOpen(false)}
          selected={selected}
          setSelected={setSelected}
          onSubmit={handleUpdate}
        />
      </div>
    </PermittedComponent>
  )
}

export default QueriesIndex
